import React, { useEffect, useState, useMemo, useRef } from "react";
import styled, { css } from "styled-components";
import PlannerService from "../services/plannerService";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "../components/calendar/planner-styles.css";
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const PlannerScreen = () => {
  const [planner, setPlanner] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewRange, setViewRange] = useState({
    start: new Date().toISOString().split("T")[0],
    end: moment().add(6, 'days').format('YYYY-MM-DD') // Default to 7 days view
  });
  const [view, setView] = useState('week');

  // Fetch planner data when view range changes
  useEffect(() => {
    const fetchPlanner = async () => {
      setLoading(true);
      setPlanner([]); // Clear existing data while loading

      try {
        console.log(`Fetching data for range: ${viewRange.start} to ${viewRange.end}`);
        
        // Generate array of dates to fetch
        const dates = [];
        const startDate = moment(viewRange.start);
        const endDate = moment(viewRange.end);
        const dayCount = endDate.diff(startDate, 'days') + 1;
        
        // for (let i = 0; i < dayCount; i++) {
        //   const date = moment(startDate).add(i, 'days').format('YYYY-MM-DD');
        //   dates.push(date);
        // }
        
        // console.log(`Fetching data for ${dates.length} days:`, dates);
        
        // // Make sequential API calls
        // const allEvents = [];
        // for (const date of dates) {
        //   console.log(`Fetching data for ${date}`);
        //   const dailyEvents = await PlannerService.getPlanner(date, date);
        //   allEvents.push(...dailyEvents);
        //   // Small delay to avoid overwhelming the API
        //   await new Promise(resolve => setTimeout(resolve, 50));
        // }

        const allEvents = await PlannerService.getPlanner(startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD'));

        console.log('All events:', allEvents);
        setPlanner(allEvents);
      } catch (error) {
        console.error("Error fetching planner data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPlanner();
  }, [viewRange.start, viewRange.end]); // Depend on the specific properties

  const localizer = momentLocalizer(moment);

  // Transform planner data to the format expected by react-big-calendar
  const calendarEvents = useMemo(() => {
    return planner.map((item) => {
      let start, end;

      // Extract just the task name without any time information
      let taskName = item.title;
      // Remove any time patterns like "10:15 AM - 11:00 AM" from the title
      taskName = taskName.replace(/^\d{1,2}:\d{2}\s*(AM|PM)\s*-\s*\d{1,2}:\d{2}\s*(AM|PM)\s*/i, "");

      try {
        if (item.start_time.includes("T")) {
          // Parse ISO string but interpret as local time (ignore Z) and add 1 hour
          const [datePart, timePart] = item.start_time
            .replace("Z", "")
            .split("T");
          const [year, month, day] = datePart.split("-").map(Number);
          const [hours, minutes, seconds] = timePart.split(":").map(Number);

          // Create date in local time zone and add 1 hour offset
          start = new Date(year, month - 1, day, hours , minutes, seconds);
        } else {
          // For simple time format, create today's date with that time
          const [hours, minutes] = item.start_time.split(":").slice(0, 2);
          start = new Date();
          start.setHours(parseInt(hours, 10) , parseInt(minutes, 10), 0);
        }
      } catch (e) {
        console.error("Error parsing start time:", e);
        start = new Date();
      }

      try {
        if (item.end_time.includes("T")) {
          // Parse ISO string but interpret as local time (ignore Z) and add 1 hour
          const [datePart, timePart] = item.end_time
            .replace("Z", "")
            .split("T");
          const [year, month, day] = datePart.split("-").map(Number);
          const [hours, minutes, seconds] = timePart.split(":").map(Number);

          // Create date in local time zone and add 1 hour offset
          end = new Date(year, month - 1, day, hours , minutes, seconds);
        } else {
          // For simple time format, create today's date with that time
          const [hours, minutes] = item.end_time.split(":").slice(0, 2);
          end = new Date();
          end.setHours(parseInt(hours, 10) , parseInt(minutes, 10), 0);
        }
      } catch (e) {
        console.error("Error parsing end time:", e);
        end = new Date();
      }

      return {
        title: taskName,
        start: start,
        end: end,
        category: item.category,
        itemType: item.item_type,
        originalTitle: item.title,
      };
    });
  }, [planner]);

  // Custom event component to display the correct time in the title
  const EventComponent = ({ event }) => {
    // Format the start and end times
    const startTime = moment(event.start).format('h:mm A');
    const endTime = moment(event.end).format('h:mm A');
    
    return (
      <div>
        {event.title}
      </div>
    );
  };

  // Handle calendar navigation and range changes
  const handleRangeChange = (range, newView) => {
    console.log('Range changed:', range);
    console.log('Current view:', view);
    
    // Handle different range formats based on the view
    let start, end;
    
    if (Array.isArray(range)) {
      // Month view returns an array of dates
      start = moment(range[0]).format('YYYY-MM-DD');
      end = moment(range[range.length - 1]).format('YYYY-MM-DD');
    } else if (range.start && range.end) {
      // Week and day views return {start, end}
      start = moment(range.start).format('YYYY-MM-DD');
      end = moment(range.end).format('YYYY-MM-DD');
    } else {
      // Default case - just use today
      start = moment().format('YYYY-MM-DD');
      end = moment().add(6, 'days').format('YYYY-MM-DD');
    }
    
    console.log(`Setting new range: ${start} to ${end}`);
    setViewRange({ start, end });
  };

  // Handle view change
  const handleViewChange = (newView) => {
    console.log('View changed to:', newView);
    setView(newView);
  };

  // Format the event title to include the correct time
  const eventPropGetter = (event) => {
    return {
      style: {
        backgroundColor:
          event.category === "Work"
            ? "#3174ad"
            : event.category === "Personal"
            ? "#33a853"
            : event.category === "Review Activities"
            ? "#f5b400"
            : "#9e9e9e",
      },
    };
  };


  return (
    <Container>
      <Header>
        <h1>Planner</h1>
        {loading && <div>Loading events...</div>}
      </Header>
      <Calendar
        localizer={localizer}
        events={calendarEvents}
        formats={{
            eventTimeRangeFormat: () => '', // Hide time display above task cards
            eventTimeRangeEndFormat: () => '',
            eventTimeRangeStartFormat: () => ''
          }}
        step={30}
        defaultView="week"
        onRangeChange={handleRangeChange}
        onView={handleViewChange}
        eventPropGetter={eventPropGetter}
        components={{
          event: EventComponent,
        }}
      />
    </Container>
  );
};

export default PlannerScreen;
